import React, { FormEvent, useEffect, useState } from 'react';
import { Grid, IconButton, InputAdornment, Typography, Stack, Box, debounce } from '@mui/material';
import { useForm } from 'react-hook-form';
import { TextField } from '@/components/forms/inputs/TextField/TextField';
import { SelectField } from '@/components/forms/inputs/SelectField/SelectField';
import { CheckboxField } from '@/components/forms/inputs/CheckBoxField/CheckboxField';
import { registerFormModel } from '@/components/forms/forms/registration/registerFormModel';
import { RegistrationStep } from '@/types/enums/forms/registration/registrationWizard';
import { getRegDataOrNonMemberPersonalData } from '@/components/forms/forms/registration/registerUtils';
import { usePhrase } from '@/hooks/general/usePhrase/usePhrase';
import { PasswordRequirements } from '@/components/forms/other/PasswordRequirements/PasswordRequirements';
import { PreviousNextButtons } from '@/components/forms/other/PreviousNextButtons/PreviousNextButtons';
import { usePersonalDetails } from '@/hooks/forms/register/usePersonalDetails';
import { useHomeQueryParams } from '@/hooks/general/useHomeQueryParams/useHomeQueryParams';
import {
    IAllRegistrationData,
    IRegistrationTemplateResponse,
    IRegistrationPersonalDetails,
} from '@/types/types/forms/registration/registration.interface';
import { DefaultPageProps } from '@/types/types/page/pages';
import { ICountryData } from '@/types/types/forms/dictionary/dictionary.interface';
import { IWalletPersonalData } from '@/types/types/forms/wallet/wallet.interface';
import { registerService } from '@/services/api/registerService/registerService';
import { useRegistrationStore } from '@/store/pages/registrationStore/registrationStore';
import { FORM_STEP_PHRASES, PERSONAL_DETAILS_PHRASES } from '@/consts/phrases/registrationPhrases';
import { useServerErrors } from '@/hooks/forms/useServerErrors';
import { localeDict } from '@/consts/languageAccordance';
import * as Sentry from '@sentry/nextjs';
import { Tooltip } from '@/components/forms/other/Tooltip/Tooltip';
import { useLocale } from '@/hooks/general/useLocale/useLocale';
import { FormGridErrors } from '@/components/forms/alerts/FormGridErrors/FormGridErrors';

import { SecureIconImage } from '@/assets/theme/default/registerImages/SecureIconImage';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import { ACCOUNT_PASSWORD_CHANGE_PHRASES } from '@/consts/phrases/accountPhrases';

interface RegistrationPersonalDetailsProps {
    registrationData?: Partial<IAllRegistrationData>;
    nonMemberPersonalData?: Partial<IWalletPersonalData>;
    registrationTemplate?: IRegistrationTemplateResponse;
    dictionary: DefaultPageProps['dictionary'];
    onPersonalDetailsSubmit: (data: Partial<IRegistrationPersonalDetails>) => void;
    onProfessionChange: (professionId: number) => void;
    revalidateRegistrationData: () => Promise<void>;
}

export const RegistrationPersonalDetailsForm = ({
    dictionary,
    registrationData,
    nonMemberPersonalData,
    registrationTemplate,
    revalidateRegistrationData,
    onPersonalDetailsSubmit,
    onProfessionChange,
}: RegistrationPersonalDetailsProps) => {
    const { t } = usePhrase();
    const { locale } = useLocale();
    const personalDetailsModel = registerFormModel[RegistrationStep.PersonalDetails];
    const { queryProfessionId, externalSourceId, externalUserId } = useHomeQueryParams();
    const persistedHowHeardId = useRegistrationStore((state) => state.howHeardId);

    const {
        firstName,
        lastName,
        email,
        emailConfirm,
        password,
        passwordConfirm,
        countryCode,
        professionId,
        howHeardId,
        preferredLanguageId,
        marketResearch,
        consents,
        age,
    } = personalDetailsModel;

    const { goToNextStep, registrationId, setRegistrationId } = useRegistrationStore((state) => ({
        goToNextStep: state.goToNextStep,
        registrationId: state.registrationId,
        setRegistrationId: state.setRegistrationId,
    }));

    const {
        countriesList,
        getCountryFilteredProfessionsList,
        howHeardList,
        preferredLangList,
        checkPreferred,
    } = usePersonalDetails({
        dictionary,
        registrationData,
        nonMemberPersonalData,
        restrictCountries: true,
    });

    const getRegOrNonMemberData = getRegDataOrNonMemberPersonalData(
        registrationData,
        nonMemberPersonalData,
    );

    const hasOnlyOneCountry = countriesList.length === 1;
    const defaultCountryCode = hasOnlyOneCountry ? String(countriesList[0].value) : null;

    const currentCountryCode =
        defaultCountryCode ||
        registrationTemplate?.countryCode ||
        getRegOrNonMemberData('countryCode') ||
        countryCodeBasedOnLocale(locale);

    const currentProfessionId =
        registrationTemplate?.professionId || registrationData?.professionId || queryProfessionId;

    const { control, handleSubmit, formState, getValues, setValue, setError, clearErrors, watch } =
        useForm<Partial<IRegistrationPersonalDetails>>({
            mode: 'onChange',
            values: {
                firstName: getRegOrNonMemberData('firstName'),
                lastName: getRegOrNonMemberData('lastName'),
                email: getRegOrNonMemberData('email'),
                emailConfirm: getRegOrNonMemberData('emailConfirm', 'email'),
                password: '',
                passwordConfirm: '',
                countryCode: currentCountryCode,
                professionId: currentProfessionId,
                preferredLanguageId:
                    registrationData?.preferredLanguageId ??
                    preferredLanguageIdBasedOnLocale(locale, dictionary.countries),
                howHeardId: persistedHowHeardId || registrationData?.howHeardId,
                marketResearch: Boolean(registrationData?.marketResearch),
                consents: Boolean(registrationData?.consents),
                age: Boolean(registrationData?.age),
                externalSourceId: externalSourceId?.toString(),
                externalUserId: externalUserId?.toString(),
            },
        });

    const { errors, isSubmitting } = formState;

    const { handleServerErrors, serverErrors } = useServerErrors({
        setError,
        clearErrors,
        getValues,
    });

    const submitData = async (values: Partial<IRegistrationPersonalDetails>) => {
        try {
            if (!registrationId) {
                const newRegistrationId = await registerService.startRegistration(values);
                setRegistrationId(newRegistrationId);
            } else {
                await registerService.putPersonalDetails(registrationId, values);
                onPersonalDetailsSubmit(values);
            }
            goToNextStep();
            await revalidateRegistrationData();
        } catch (err) {
            handleServerErrors(err);
        }
    };

    const synchronisePreferredLanguage = async (
        countryCodeFormVal: string | FormEvent<HTMLDivElement>,
    ) => {
        try {
            await checkPreferred(countryCodeFormVal);
            const localeCountryCode = locale ? localeDict[locale].country.code3 : undefined;
            const selectedLocaleHasPreferredLanguages = checkForPreferredLanguages(
                locale,
                dictionary.countries,
            );

            if (localeCountryCode === countryCodeFormVal && selectedLocaleHasPreferredLanguages) {
                setValue(
                    'preferredLanguageId',
                    preferredLanguageIdBasedOnLocale(locale, dictionary.countries),
                );
            } else {
                setValue('preferredLanguageId', null);
            }
        } catch (err) {
            Sentry.captureException(err);
        }
    };

    const clearProfessionIdIfInvalid = () => {
        const currentProfessionId = getValues('professionId');
        const isProfessionIdValid = professionsList.some(
            (item) => item.value === currentProfessionId,
        );

        if (!isProfessionIdValid) {
            setValue('professionId', null);
        }
    };

    const countryCodeFormVal = watch('countryCode');
    const professionsList = getCountryFilteredProfessionsList(countryCodeFormVal);

    const handleProfessionChange = (val: number | string | FormEvent<HTMLDivElement>) => {
        if (typeof val === 'number') {
            onProfessionChange(val);
            setValue('professionId', val);
        }
    };

    useEffect(() => {
        if (countryCodeFormVal) {
            void synchronisePreferredLanguage(countryCodeFormVal);
            clearProfessionIdIfInvalid();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [countryCodeFormVal]);

    const MatchIcon = () => (
        <InputAdornment position="end" sx={{ marginRight: '-2px' }}>
            <CheckCircleOutlineOutlinedIcon
                data-testid="match-icon"
                sx={(theme) => ({
                    fontSize: '1.125rem',
                    color: theme.palette.success.main,
                })}
            />
        </InputAdornment>
    );

    const [emailsMatch, setEmailsMatch] = useState(false);
    const [passwordsMatch, setPasswordsMatch] = useState(false);

    const checkEmailsMatch = debounce(() => {
        const [email, emailConfirm] = getValues(['email', 'emailConfirm']);
        setEmailsMatch(!!email && !!emailConfirm && email === emailConfirm);
    }, 300);

    const checkPasswordsMatch = debounce(() => {
        const [password, passwordConfirm] = getValues(['password', 'passwordConfirm']);
        setPasswordsMatch(!!password && !!passwordConfirm && password === passwordConfirm);
    }, 300);

    useEffect(() => {
        checkEmailsMatch();
        checkPasswordsMatch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <form onSubmit={handleSubmit(submitData)} noValidate>
            <Grid container spacing={2}>
                <FormGridErrors
                    errors={serverErrors}
                    gridSx={{ pb: 4 }}
                    generalErrorId="message_box"
                />
                <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                        control={control}
                        name={firstName.name}
                        label={firstName.label}
                        type="text"
                        required
                        fullWidth
                        error={errors.firstName}
                        data-testid="firstname-input"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                        control={control}
                        name={lastName.name}
                        label={lastName.label}
                        type="text"
                        required
                        fullWidth
                        error={errors.lastName}
                        data-testid="lastname-input"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                        control={control}
                        name={email.name}
                        label={email.label}
                        type="email"
                        required
                        fullWidth
                        error={errors.email}
                        onChange={checkEmailsMatch}
                        data-testid="email-input"
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                        control={control}
                        name={emailConfirm.name}
                        label={emailConfirm.label}
                        type="email"
                        required
                        fullWidth
                        error={errors.emailConfirm}
                        onChange={checkEmailsMatch}
                        slotProps={{
                            input: {
                                endAdornment: emailsMatch ? <MatchIcon /> : null,
                            },
                        }}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                        control={control}
                        name={password.name}
                        label={password.label}
                        type="password"
                        role="textbox"
                        required
                        fullWidth
                        error={errors.password}
                        onChange={checkPasswordsMatch}
                        data-testid="password-input"
                        autoComplete="new-password"
                        slotProps={{
                            input: {
                                endAdornment: (
                                    <InputAdornment position="end" sx={{ marginRight: '-8px' }}>
                                        <Tooltip
                                            title={<PasswordRequirements tooltipText />}
                                            enterTouchDelay={0}
                                        >
                                            <IconButton
                                                aria-label={t(
                                                    ACCOUNT_PASSWORD_CHANGE_PHRASES.PASS_REQUIREMENTS_TITLE,
                                                    { asString: true },
                                                )}
                                                color="inherit"
                                                size="small"
                                                sx={{ cursor: 'help' }}
                                            >
                                                <HelpOutlineIcon sx={{ fontSize: '18px' }} />
                                            </IconButton>
                                        </Tooltip>
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                        control={control}
                        name={passwordConfirm.name}
                        label={passwordConfirm.label}
                        type="password"
                        role="textbox"
                        required
                        fullWidth
                        error={errors.passwordConfirm}
                        onChange={checkPasswordsMatch}
                        data-testid="passwordConfirm-input"
                        autoComplete="new-password"
                        slotProps={{
                            input: {
                                endAdornment: passwordsMatch ? <MatchIcon /> : null,
                            },
                        }}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <SelectField
                        control={control}
                        name={countryCode.name}
                        label={countryCode.label}
                        data={countriesList}
                        fullWidth
                        required
                        error={errors.countryCode}
                        data-testid="countryCode-input"
                        disabled={hasOnlyOneCountry}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <SelectField
                        control={control}
                        name={professionId.name}
                        label={professionId.label}
                        data={professionsList}
                        fullWidth
                        required
                        error={errors.professionId}
                        data-testid="professionId-input"
                        onChange={handleProfessionChange}
                    />
                </Grid>
                {preferredLangList && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <SelectField
                            control={control}
                            name={preferredLanguageId.name}
                            label={preferredLanguageId.label}
                            data={preferredLangList}
                            required
                            fullWidth
                            error={errors.preferredLanguageId}
                            data-testid="preferredLanguageId-input"
                        />
                    </Grid>
                )}
                {!persistedHowHeardId && (
                    <Grid size={12}>
                        <SelectField
                            control={control}
                            name={howHeardId.name}
                            label={howHeardId.label}
                            data={howHeardList}
                            error={errors.howHeardId}
                            fullWidth
                        />
                    </Grid>
                )}
                <Grid size={12}>
                    <CheckboxField
                        control={control}
                        name={marketResearch.name}
                        label={PERSONAL_DETAILS_PHRASES.MARKET_RESEARCH_OPT_IN}
                        error={errors.marketResearch}
                        required
                    />
                    <CheckboxField
                        control={control}
                        name={consents.name}
                        label={consents.label}
                        error={errors.consents}
                        required
                    />
                    <CheckboxField
                        control={control}
                        name={age.name}
                        label={age.label}
                        error={errors.age}
                        required
                    />
                </Grid>
                <Grid size={12}>
                    <Stack sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
                        <Stack direction="row" spacing={2}>
                            <Box sx={{ flexShrink: 0 }}>
                                <SecureIconImage />
                            </Box>
                            <Typography
                                variant="subtitle3"
                                className="privacyDescription"
                                sx={(theme) => ({
                                    color: theme.colors.other.text.gray,
                                })}
                            >
                                {t(PERSONAL_DETAILS_PHRASES.PHOTO_TEXT)}
                            </Typography>
                        </Stack>
                        <Box
                            sx={{
                                display: 'flex',
                                width: { xs: '100%', sm: 'auto' },
                                justifyContent: { xs: 'center', sm: 'flex-end' },
                                alignItems: { xs: 'flex-end', sm: 'flex-start' },
                                pl: { xs: 0, sm: 4 },
                                pt: { xs: 4, sm: 0 },
                            }}
                        >
                            <PreviousNextButtons
                                hidePrevious={true}
                                nextDisabled={isSubmitting}
                                previousDisabled={isSubmitting}
                                nextLabel={FORM_STEP_PHRASES.REGISTER}
                            />
                        </Box>
                    </Stack>
                </Grid>
            </Grid>
        </form>
    );
};

const countryCodeBasedOnLocale = (locale: string | undefined) => {
    const countryCode = locale ? localeDict[locale].country.code3 : undefined;
    if (countryCode?.toLowerCase() === 'int') return;
    return countryCode;
};

const checkForPreferredLanguages = (locale: string | undefined, countriesData: ICountryData[]) => {
    const countryCode = locale ? localeDict[locale].country.code3 : undefined;
    const selectedCountry = countriesData.filter((country) => country?.countryCode === countryCode);
    const hasPreferredLanguages = selectedCountry[0]?.preferredLanguages?.length > 0;
    return hasPreferredLanguages;
};

const preferredLanguageIdBasedOnLocale = (
    locale: string | undefined,
    countriesData: ICountryData[],
) => {
    const hasPreferredLanguages = checkForPreferredLanguages(locale, countriesData);

    const languageId = locale ? localeDict[locale].language.id : undefined;
    if (hasPreferredLanguages) {
        return languageId;
    } else {
        return undefined;
    }
};
